import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { shareService, SharedContent } from '../../services/shareService';
import { API_URLS } from '../../config/Constants';
import SharedTeamsReport from './SharedTeamsReport';
import SharedPlayersReport from './SharedPlayersReport';
import SharedPlayersLeaderboard from './SharedPlayersLeaderboard';
import SharedPlayerStatSheet from './SharedPlayerStatSheet';

const SharedContentViewer: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareData, setShareData] = useState<SharedContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourceInfo, setResourceInfo] = useState<{
    name: string;
    type: string;
  } | null>(null);

  const fetchResourceInfo = useCallback(async (content: SharedContent) => {
    try {
      let url = '';
      let type = '';
      if (content.contentType === 'season') {
        url = `${API_URLS.BASE_URL}/share/seasons/${content.seasonId}?clientId=${content.clientId}`;
        type = 'Season';
      } else if (content.contentType === 'game') {
        url = `${API_URLS.BASE_URL}/share/games/${content.gameId}?clientId=${content.clientId}`;
        type = 'Game';
      } else if (content.contentType === 'leaderboard') {
        setResourceInfo({
          name: content.name || 'Players Leaderboard',
          type: 'Leaderboard',
        });
        return;
      } else if (content.shareType === 'player') {
        if (content.seasonId && content.clientId) {
          url = `${API_URLS.BASE_URL}/share/seasons/${content.seasonId}?clientId=${content.clientId}`;
          type = 'Season';
        } else {
          setResourceInfo({
            name: content.name || 'Player Stat Sheet',
            type: 'Player',
          });
          return;
        }
      } else {
        setResourceInfo({
          name: content.name || 'Shared Report',
          type: 'Shared',
        });
        return;
      }

      const response = await shareService.publicFetch(url);
      if (response.data.success) {
        const data = response.data.data;
        setResourceInfo({
          name: data.name || data.opponent || 'Unknown',
          type: type,
        });
      }
    } catch (error) {
      console.error('Error fetching resource info:', error);
    }
  }, []);

  const loadSharedContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const content = await shareService.getSharedContent(shareId!);
      setShareData(content);

      // Fetch resource information (season or game details)
      await fetchResourceInfo(content);
    } catch (err) {
      console.error('Error loading shared content:', err);
      setError('Failed to load shared content. The link may be invalid.');
    } finally {
      setLoading(false);
    }
  }, [shareId, fetchResourceInfo]);

  useEffect(() => {
    if (!shareId) {
      setError('Invalid share link');
      setLoading(false);
      return;
    }

    loadSharedContent();
  }, [loadSharedContent, shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sd-navy mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shared content...</p>
        </div>
      </div>
    );
  }

  if (error || !shareData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.884-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Content Not Found</h1>
          <p className="text-gray-600 mb-6">{error || "The shared content you're looking for could not be found."}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-sd-navy text-white px-6 py-2 rounded-lg hover:bg-sd-navy transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderHeader = () => {
    return (
      <div className="bg-white shadow-sm border-b">
        <div className="w-full mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 sm:py-4 gap-3">
            <div className="flex items-center space-x-3 sm:space-x-4">
              {shareData?.client?.logo && (
                <img
                  src={`${process.env.REACT_APP_UPLOAD_URL}uploads/clients/${shareData.client.logo}`}
                  alt={shareData.client.name}
                  className="h-10 sm:h-12 w-auto flex-shrink-0"
                />
              )}
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                  {shareData?.client?.name || shareData?.name || 'STATDOG'}
                </h1>
                {shareData?.shareType === 'player' && (
                  <div className="text-xs text-gray-500">Player Stat Sheet</div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="text-left sm:text-right">
                <div className="text-sm text-gray-500">
                  {resourceInfo?.type}: <span className="font-medium">{resourceInfo?.name}</span>
                </div>
                <div className="text-xs text-gray-400">Powered by STATDOG</div>
              </div>
              <img src={`/images/logo-notext.png`} alt="statdog logo" className="h-10 sm:h-12 w-auto flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Render the appropriate report based on shareType
    if (shareData?.contentType === 'leaderboard' && shareData?.shareType === 'players') {
      return <SharedPlayersLeaderboard shareData={shareData} />;
    }

    if (shareData?.shareType === 'player') {
      return <SharedPlayerStatSheet shareData={shareData} />;
    }

    if (shareData?.shareType === 'players') {
      return <SharedPlayersReport shareData={shareData} />;
    } else {
      return <SharedTeamsReport shareData={shareData} />;
    }
  };

  return (
    <div className="bg-gray-50">
      {renderHeader()}
      <div className="relative">{renderContent()}</div>
    </div>
  );
};

export default SharedContentViewer;
